import { useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, ChevronRight, RefreshCw, FlaskConical } from "lucide-react";

const cell = "py-1 px-2 font-mono text-[11px] text-right";
const head = "py-1 px-2 font-mono text-[9px] uppercase tracking-widest text-zinc-600 text-right";
const fmtP = (v) => (v == null ? "—" : `${v}%`);

/** Admin-only: tests the user's weekly-contract level-depreciation hypothesis. */
export default function LevelDecayStudy() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (force = false) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/level-decay${force ? "?force=true" : ""}`);
      setData(d);
    } catch (e) { console.warn("level-decay load failed:", e?.message || e); }
    finally { setLoading(false); }
  };

  const toggle = () => { setOpen((o) => !o); if (!open && !data) load(); };

  return (
    <section className="bg-[#121212] border border-zinc-800" data-testid="level-decay-study">
      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-zinc-900/40"
              data-testid="level-decay-toggle">
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-400 font-mono">
          <FlaskConical className="h-3.5 w-3.5 text-[#F59E0B]" /> Level Decay Study
          <span className="text-zinc-600">· weekly contract hypothesis · admin only</span>
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-zinc-500 leading-relaxed max-w-2xl">
              Hypothesis: level penetration = <span className="text-[#F59E0B] font-mono">25 / 50 / 75 / 100 / 105%</span> of
              the entry–SL range on Wed→Tue(expiry). Measured on NIFTY / BANKNIFTY / SENSEX signals — 5-min candles, post-signal reversal pivots only.
            </p>
            <button onClick={() => load(true)} disabled={loading}
                    className="text-zinc-500 hover:text-white p-1" data-testid="level-decay-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          {loading && !data && <div className="text-[10px] font-mono text-zinc-600 py-3">◢ crunching {`—`} zero LLM credits used…</div>}
          {data && (
            <>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Penetration depth by weekday of touch</div>
                <table className="w-full text-left border border-zinc-800">
                  <thead><tr className="border-b border-zinc-800">
                    <th className={`${head} text-left`}>Day</th><th className={head}>Contract D</th>
                    <th className={head}>Days obs.</th><th className={head}>Touch rate</th>
                    <th className={head}>Median depth</th><th className={head}>Mean depth</th>
                    <th className={head}>Your hypothesis</th>
                  </tr></thead>
                  <tbody>
                    {data.depth_by_weekday.map((r) => {
                      const close = r.median_depth_pct != null && Math.abs(r.median_depth_pct - r.hypothesis_pct) <= 20;
                      return (
                        <tr key={r.weekday} className="border-b border-zinc-900/60" data-testid={`ld-row-${r.weekday}`}>
                          <td className={`${cell} text-left text-zinc-200`}>{r.weekday}</td>
                          <td className={cell}>D{r.contract_day}</td>
                          <td className={cell}>{r.days_observed}</td>
                          <td className={cell}>{r.touch_rate == null ? "—" : `${Math.round(r.touch_rate * 100)}%`}</td>
                          <td className={`${cell} font-bold ${close ? "text-[#22C55E]" : "text-zinc-200"}`}>{fmtP(r.median_depth_pct)}</td>
                          <td className={cell}>{fmtP(r.mean_depth_pct)}</td>
                          <td className={`${cell} text-[#F59E0B]`}>{fmtP(r.hypothesis_pct)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Depth by days since signal</div>
                  <table className="w-full text-left border border-zinc-800">
                    <thead><tr className="border-b border-zinc-800">
                      <th className={`${head} text-left`}>D+</th><th className={head}>Obs.</th><th className={head}>Reversals</th><th className={head}>Median depth</th>
                    </tr></thead>
                    <tbody>
                      {data.depth_by_offset.map((r) => (
                        <tr key={r.days_since_signal} className="border-b border-zinc-900/60">
                          <td className={`${cell} text-left`}>D+{r.days_since_signal}</td>
                          <td className={cell}>{r.days_observed}</td>
                          <td className={cell}>{r.reversals}</td>
                          <td className={`${cell} font-bold text-zinc-200`}>{fmtP(r.median_depth_pct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Outcomes by signal contract-day</div>
                  <table className="w-full text-left border border-zinc-800">
                    <thead><tr className="border-b border-zinc-800">
                      <th className={`${head} text-left`}>Day</th><th className={head}>N</th><th className={head}>Win</th><th className={head}>SL</th><th className={head}>Win rate</th>
                    </tr></thead>
                    <tbody>
                      {data.outcomes_by_contract_day.map((r) => (
                        <tr key={r.weekday} className="border-b border-zinc-900/60">
                          <td className={`${cell} text-left`}>{r.weekday} (D{r.contract_day})</td>
                          <td className={cell}>{r.n}</td>
                          <td className={`${cell} text-[#22C55E]`}>{r.win}</td>
                          <td className={`${cell} text-[#EF4444]`}>{r.sl}</td>
                          <td className={`${cell} font-bold`}>{fmtP(r.win_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-2 text-[11px] font-mono text-zinc-400" data-testid="ld-t1-reversal">
                    T1 reversal: <span className="text-[#F59E0B] font-bold">{fmtP(data.t1_reversal.reversal_from_t1_rate_pct)}</span> of T1 hits reversed before T2
                    ({data.t1_reversal.hit_t1_then_reversed} reversed · {data.t1_reversal.ran_to_t2} ran to T2)
                  </div>
                </div>
              </div>
              <ul className="text-[10px] text-zinc-600 space-y-0.5 list-disc pl-4">
                {data.caveats.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
